import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Users, Briefcase, FileText, LogOut, User, Database, UsersRound, ChevronsLeft
} from "lucide-react";

const NAV_BY_ROLE = {
  admin: [
    { to: "/", label: "Overview", icon: LayoutDashboard, exact: true },
    { to: "/leads", label: "Leads", icon: Database },
    { to: "/positions", label: "Open Positions", icon: Briefcase },
    { to: "/candidates", label: "Candidates", icon: UsersRound },
    { to: "/users", label: "Users", icon: Users },
    { to: "/profile", label: "Profile", icon: User },
  ],
  recruiter: [
    { to: "/", label: "Open Positions", icon: Briefcase, exact: true },
    { to: "/my-submissions", label: "My Submissions", icon: FileText },
    { to: "/profile", label: "Profile", icon: User },
  ],
  research_analyst: [
    { to: "/", label: "Leads", icon: Database, exact: true },
    { to: "/positions", label: "Open Positions", icon: Briefcase },
    { to: "/profile", label: "Profile", icon: User },
  ],
};

const ROLE_LABEL = {
  admin: "Admin",
  recruiter: "Recruiter",
  research_analyst: "Research Analyst",
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  if (!user) return null;
  const nav = NAV_BY_ROLE[user.role] || [];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`${collapsed ? "w-16" : "w-64"} fixed left-0 top-0 bottom-0 bg-white border-r border-gray-200 z-30 flex flex-col transition-all duration-200`}
      >
        <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"} px-4 py-5 border-b border-gray-100`}>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-brand grid place-items-center text-white font-bold font-heading">N</div>
              <div className="font-heading font-bold text-base tracking-tight">Neoventra</div>
            </div>
          )}
          {collapsed && <div className="w-8 h-8 rounded-md bg-brand grid place-items-center text-white font-bold font-heading">N</div>}
          {!collapsed && (
            <button
              data-testid="sidebar-collapse-button"
              onClick={() => setCollapsed(true)}
              className="text-gray-400 hover:text-gray-700 p-1"
              title="Collapse sidebar"
            >
              <ChevronsLeft size={16} />
            </button>
          )}
        </div>

        <nav className="flex-1 py-4 space-y-0.5">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-brand-light text-brand"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  } ${collapsed ? "justify-center" : ""}`
                }
              >
                <Icon size={18} strokeWidth={2} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className={`border-t border-gray-100 p-3 ${collapsed ? "" : ""}`}>
          {!collapsed && (
            <div className="px-2 mb-2">
              <div className="text-sm font-semibold text-gray-900 truncate" data-testid="sidebar-user-name">{user.name}</div>
              <div className="text-xs text-gray-500 truncate">{user.email}</div>
              <div className="mt-1.5">
                <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brand-light text-brand">
                  {ROLE_LABEL[user.role] || user.role}
                </span>
              </div>
            </div>
          )}
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-700 rounded-md transition-colors ${collapsed ? "justify-center" : ""}`}
            title="Logout"
          >
            <LogOut size={16} />
            {!collapsed && <span>Logout</span>}
          </button>
          {collapsed && (
            <button
              data-testid="sidebar-expand-button"
              onClick={() => setCollapsed(false)}
              className="mt-2 w-full text-gray-400 hover:text-gray-700 text-xs"
              title="Expand"
            >
              →
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className={`${collapsed ? "ml-16" : "ml-64"} flex-1 min-h-screen transition-all duration-200`}>
        <Outlet />
      </main>
    </div>
  );
}
